import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import type { RecommendationAction } from 'modules/timeoff/components/LeaveApprovalRecommendation/RecommendationAction'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { trackApprovalRecommendation } from 'modules/timeoff/lib/tracking'
import type Leave from 'modules/timeoff/store/models/Leave'

import { useApprovalFlowStepsCount } from './useApprovalFlowStepsCount'

interface AcceptButtonProps {
  leave: Leave
  form: LeaveForm
  onFetchStats: () => void
  canApprove: boolean
  canApproveEntireRequest: boolean
  canUpdate: boolean
  isCurrentApprover?: boolean
  isCurrentApproverLoading?: boolean
  approvalFlowId?: number
  disabled: boolean
  fromShifts?: boolean
  onClose: () => void
  onlySubmit?: boolean
  employeeId: number
  recommendationAction?: RecommendationAction
}

const AcceptButton = ({
  leave,
  form,
  onFetchStats,
  canApprove,
  canApproveEntireRequest,
  canUpdate,
  isCurrentApprover,
  isCurrentApproverLoading,
  approvalFlowId,
  disabled,
  fromShifts = false,
  onClose,
  onlySubmit = false,
  employeeId,
  recommendationAction,
}: AcceptButtonProps) => {
  const { closePortal } = usePortal()
  const { fetchData } = useLeaves()

  const { loading: stepApproversCountLoading, stepsCount } =
    useApprovalFlowStepsCount(approvalFlowId)

  const isOneStepApprovalFlow = stepsCount === 1

  const loading = isCurrentApproverLoading || stepApproversCountLoading

  const submitButton = (
    <Button disabled={disabled} submit>
      {i18n.t('time_off.admin.edit_approved.submit')}
    </Button>
  )

  if (loading || leave.isApproved() || !(canApprove || isCurrentApprover)) {
    return submitButton
  }

  const handleSuccess = async (action: string, value: Leave) => {
    onFetchStats()
    leave.set(value)
    fetchData(employeeId)
    closePortal()
    onClose()
    track(trackingEvents.timeoff.reviewTimeOffRequestSubmit, {
      request_action: action,
      from_shifts: fromShifts,
    })

    if (recommendationAction) {
      trackApprovalRecommendation('approve', recommendationAction)
    }
  }

  const performAction = async (action: string, rpcMethod: string) => {
    if (canUpdate) {
      const { success } = await asyncAction(() => form.save(leave))

      if (!success) {
        return
      }
    }

    const { success, value } = await asyncAction(() => leave.rpc(rpcMethod))

    if (success) handleSuccess(action, value)
  }

  const approveStep = async () => {
    await performAction('approve', 'approve')
  }

  const approveEntireRequest = async () => {
    await performAction('approve_all', 'approve_all')
  }

  const approveStepTitle = i18n.t('time_off.admin.review.approve_options.step')
  const approveGlobalTitle = i18n.t('time_off.admin.review.approve_options.global')
  const defaultApproveActionTitle = isCurrentApprover ? approveStepTitle : approveGlobalTitle
  const approveTitle = isOneStepApprovalFlow ? i18n.t('common.approve') : defaultApproveActionTitle

  const defaultAction =
    isCurrentApprover ? approveStep : canApproveEntireRequest ? approveEntireRequest : approveStep
  const isRecommendationWithShifts = onlySubmit

  if (isRecommendationWithShifts) {
    return (
      <Button type="positive" submit>
        {approveTitle}
      </Button>
    )
  }

  if (isOneStepApprovalFlow || !canApprove) {
    return (
      <Button type="positive" onClick={defaultAction}>
        {approveTitle}
      </Button>
    )
  }

  if (!isCurrentApprover && !canApproveEntireRequest) {
    return (
      <Button type="positive" onClick={approveStep}>
        {approveStepTitle}
      </Button>
    )
  }

  return (
    <Button.Split label={defaultApproveActionTitle} type="positive" onClick={defaultAction}>
      <Button.Split.Option disabled={!isCurrentApprover} onClick={approveStep}>
        {approveStepTitle}
      </Button.Split.Option>
      <Button.Split.Option
        onClick={approveEntireRequest}
        disabled={!canApproveEntireRequest}
      >
        {approveGlobalTitle}
      </Button.Split.Option>
    </Button.Split>
  )
}

export default observer(AcceptButton)
